<template>
  <NuxtLayout>
    <NuxtPage />
  </NuxtLayout>
  <GlobalLoader />
  <Toaster />
  <ToastSocket />
  <template v-if="isUserLoggedIn">
    <NewConsignmentWizard />
    <NewShipmentWizard />
    <FormWizard />
    <WizardCreateTicket />
    <WizardInviteUsers />
    <WizardPrinter />

    <ClientOnly>
      <template v-if="!isTestEnvironment() && !isSkippedRoute">
        <ModalLegal
          v-if="hasPendingLegal"
          :pending-document="pendingDocument"
          :loading="documentsLoading"
          @update-accept="(val) => acceptDocument(val)"
        />
        <ModalConsent v-else-if="canShowConsentModal" :key="consentModalKey" />
        <ModalPresentation v-if="canShowPresentationModal" />
        <ModalFeatures v-if="canShowFeaturesModal" />
      </template>
      <Dialog />
      <GlobalSearch />
      <ModalSuspended />
    </ClientOnly>
  </template>
</template>
<script setup lang="ts">
  import { isTestEnvironment } from "./utils/misc";

  const { isUserLoggedIn, isExternalAccess } = useAuth();
  const workspaceStore = useWorkspacesStore();
  const { setWorkspace } = workspaceStore;
  const { workspaces, hasMultipleWorkspaces, accountId } = storeToRefs(workspaceStore);
  const { acceptDocument, hasPendingLegal, pendingDocument, documentsLoading } = useLegalDocuments();
  const { fetchServices } = useServicesStore();
  const { fetchDangerousGoodsTemplates, clearDangerousGoodsTemplates } = useOrderlines();
  const { getLegalDocuments } = useLegalDocuments();
  const { setupBaseListeners } = useEcho();
  const { setupBaseListeners: setupBaseListenersSocket } = useSocket();

  // #region Skipped Routes for Modals
  const route = useRoute();
  const isSkippedRoute = computed(() => {
    const skippedRoutes = [
      "auth-login",
      "auth-logout",
      "auth-register",
      "auth-register-user",
      "auth-register-workspace",
      "auth-reset-process",
      "auth-reset-request",
    ];
    return skippedRoutes.includes(route.name as string);
  });

  // #region Modal Priority Logic - Only show one modal at a time
  // Priority: ModalLegal > ModalConsent > ModalPresentation > ModalFeatures
  const { needsRerender, consents } = useConsentModal();
  const { currentBoardingGuide } = useBoardingGuides();

  const canShowConsentModal = computed(() => !hasPendingLegal.value && !isExternalAccess.value && !!accountId.value);
  const canShowPresentationModal = computed(() => !hasPendingLegal.value && consents.value.length === 0);
  const canShowFeaturesModal = computed(() => canShowPresentationModal.value && !currentBoardingGuide.value);
  // #endregion

  // #region Consent Modal Rerender Logic
  const consentModalKey = ref(Date.now());

  // Watch for rerender trigger and update key to force component recreation
  watch(needsRerender, (value) => {
    if (value) {
      consentModalKey.value = Date.now();
      nextTick(() => {
        needsRerender.value = false;
      });
    }
  });
  // #endregion

  onMounted(() => {
    if (import.meta.client) {
      //add current os to the body class for styling ie. ios, android, windows, mac
      document.body.classList.add(getOS().toLowerCase());

      // Set up MutationObserver to detect if consent modal is removed or hidden from DOM
      let modalWasPresent = false;

      const observer = new MutationObserver(() => {
        const consentModal = document.querySelector(".consent-modal");

        if (consentModal) {
          modalWasPresent = true;

          // Check if modal is being hidden via CSS or style attributes
          const computedStyle = window.getComputedStyle(consentModal);
          const parentModal = consentModal.closest('[role="dialog"]');
          const parentStyle = parentModal ? window.getComputedStyle(parentModal) : null;

          // Restore visibility if user tries to hide it
          if (
            consents.value.length > 0 &&
            (computedStyle.display === "none" ||
              computedStyle.visibility === "hidden" ||
              computedStyle.opacity === "0" ||
              (parentStyle &&
                (parentStyle.display === "none" || parentStyle.visibility === "hidden" || parentStyle.opacity === "0")))
          ) {
            // Force rerender to restore modal
            needsRerender.value = true;
          }
        } else if (modalWasPresent && consents.value.length > 0) {
          // Modal was present, now it's gone, and we still have pending consents
          modalWasPresent = false;
          needsRerender.value = true;
        }
      });

      // Observe the entire document for node removals, attribute changes, and style changes
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style", "class"],
      });

      // Clean up observer on unmount
      onBeforeUnmount(() => {
        observer.disconnect();
      });
    }
  });

  EventBus.$on("workspace-changed", () => {
    clearDangerousGoodsTemplates();
    Promise.all([fetchServices(), getLegalDocuments(), fetchDangerousGoodsTemplates()]);
    setTimeout(() => {
      setupBaseListeners();
      setupBaseListenersSocket();
    }, 1000);
  });

  const onKeyPress = (e: KeyboardEvent) => {
    if (!/\d/.test(e.key)) return;
    if (!hasMultipleWorkspaces.value) return;
    if (e.key === "0") return;
    if (route.fullPath.includes("settings")) return;

    //if development environment, use cmd+k to open the modal
    if (getOS() === "Mac") {
      if (e.metaKey) {
        setWorkspace(workspaces.value[parseInt(e.key) - 1]);
        e.preventDefault();
      }
    } else {
      if (e.ctrlKey) {
        setWorkspace(workspaces.value[parseInt(e.key) - 1]);
        e.preventDefault();
      }
    }
  };

  watch(
    hasMultipleWorkspaces,
    (value) => {
      if (value) {
        document.addEventListener("keydown", onKeyPress);
      } else {
        document.removeEventListener("keydown", onKeyPress);
      }
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    document.removeEventListener("keydown", onKeyPress);
  });
</script>
<style>
  html,
  body,
  #__nuxt,
  #__layout {
    height: 100% !important;
    width: 100% !important;
  }
</style>
